import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSignedPhotoUrl, useSignedPhotoUrls, extractPhotoPath } from "@/lib/photoUrl";
import { ETHNICITY, LOCATION_DFW, CITIZENSHIP_STATUS, MARITAL_STATUS } from "@/lib/enums";
import { PhotoUploadError, uploadProfilePhoto } from "@/lib/photoUpload";
import {
  PROFILE_PHOTO_FIELD_KEY,
  isRequiredFieldMissing,
} from "@/lib/requiredFields";

const schema = z.object({
  first_name: z.string().trim().min(2, "Name must be at least 2 characters").max(50, "Name must be 50 characters or less"),
  age: z.number().min(18, "Must be at least 18").max(60, "Must be under 60"),
  gender: z.enum(["male", "female"], { errorMap: () => ({ message: "Gender is required" }) }),
  height_feet: z.number().min(4, "Height feet must be between 4 and 7").max(7, "Height feet must be between 4 and 7"),
  height_inches: z.number().min(0, "Height inches must be between 0 and 11").max(11, "Height inches must be between 0 and 11"),
  ethnicity: z.string().min(1, "Ethnicity is required"),
  location: z.string().trim().min(2, "Location is required").max(100, "Location must be 100 characters or less"),
  willing_to_relocate: z.boolean(),
  citizenship_status: z.string().min(1, "Citizenship status is required"),
  marital_status: z.string().min(1, "Marital status is required"),
  has_children: z.boolean(),
  children_count: z.number().min(0, "Children count must be positive").max(10, "Children count must be 10 or less").optional(),
});

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  saving: boolean;
  disablePhotoUpload?: boolean;
  continueLabel?: string;
  loadingLabel?: string;
}

export function Step1BasicInfo({ data, onComplete, saving, disablePhotoUpload = false, continueLabel = "Save & Continue", loadingLabel = "Saving..." }: Props) {
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  // Store storage paths (relative). Legacy values may be full public URLs;
  // extractPhotoPath normalises them so display + delete still work.
  const [profilePicture, setProfilePicture] = useState<string | undefined>(
    extractPhotoPath(data.profile_picture_url) ?? undefined,
  );
  const [galleryPictures, setGalleryPictures] = useState<string[]>(() => {
    const primaryPath = extractPhotoPath(data.profile_picture_url);
    return (data.profile_pictures || [])
      .map((p) => extractPhotoPath(p))
      .filter((p): p is string => !!p && p !== primaryPath);
  });

  // Mint signed URLs for display. These are short-lived; the form re-mints
  // automatically when the underlying path changes.
  const profilePictureSignedUrl = useSignedPhotoUrl(profilePicture);
  const gallerySignedUrls = useSignedPhotoUrls(galleryPictures);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: data.first_name,
      age: data.age,
      gender: data.gender,
      height_feet: data.height_feet || 5,
      height_inches: data.height_inches || 6,
      ethnicity: data.ethnicity,
      location: data.location,
      willing_to_relocate: data.willing_to_relocate || false,
      citizenship_status: data.citizenship_status,
      marital_status: data.marital_status,
      has_children: data.has_children || false,
      children_count: data.children_count || 0,
    },
  });

  // Sync form with prop changes (e.g., from Quick Import)
  useEffect(() => {
    reset({
      first_name: data.first_name,
      age: data.age,
      gender: data.gender,
      height_feet: data.height_feet || 5,
      height_inches: data.height_inches || 6,
      ethnicity: data.ethnicity,
      location: data.location,
      willing_to_relocate: data.willing_to_relocate || false,
      citizenship_status: data.citizenship_status,
      marital_status: data.marital_status,
      has_children: data.has_children || false,
      children_count: data.children_count || 0,
    });
  }, [data, reset]);

  const gender = watch("gender");
  const hasChildren = watch("has_children");

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingProfile(true);

    try {
      // Store the storage path, not a URL. The bucket is private; reads go
      // through createSignedUrl (see useSignedPhotoUrl above).
      const fileName = await uploadProfilePhoto(file);
      setProfilePicture(fileName);
      toast.success("Profile picture uploaded");
    } catch (error) {
      console.error("upload.profile_picture_failed");
      toast.error(
        error instanceof PhotoUploadError
          ? error.message
          : "Failed to upload profile picture",
      );
    } finally {
      setUploadingProfile(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!profilePicture) return;

    try {
      // profilePicture is a storage path; extractPhotoPath also handles legacy
      // full URLs that may still exist in older drafts.
      const path = extractPhotoPath(profilePicture);
      if (path) {
        await supabase.storage.from('profile-pictures').remove([path]);
      }

      setProfilePicture(undefined);
      toast.success("Profile picture removed");
    } catch (error) {
      console.error("upload.profile_picture_remove_failed");
      toast.error("Failed to remove picture");
    }
  };

  const handleGalleryUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = 5 - galleryPictures.length;
    if (files.length > remainingSlots) {
      toast.error(`Maximum ${remainingSlots} more pictures allowed`);
      return;
    }

    setUploadingGallery(true);
    const uploadedPaths: string[] = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/gallery_${Date.now()}-${i}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('profile-pictures')
          .upload(fileName, file, { upsert: true });

        if (uploadError) throw uploadError;

        // Store storage path; reads go through createSignedUrl.
        uploadedPaths.push(fileName);
      }

      setGalleryPictures([...galleryPictures, ...uploadedPaths]);
      toast.success(`${uploadedPaths.length} picture(s) added to gallery`);
    } catch (error) {
      console.error("upload.gallery_failed");
      toast.error("Failed to upload pictures");
    } finally {
      setUploadingGallery(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleRemoveGalleryPicture = async (pathOrUrl: string) => {
    try {
      const path = extractPhotoPath(pathOrUrl);
      if (path) {
        await supabase.storage.from('profile-pictures').remove([path]);
      }

      setGalleryPictures(galleryPictures.filter((p) => p !== pathOrUrl));
      toast.success("Picture removed from gallery");
    } catch (error) {
      console.error("upload.gallery_remove_failed");
      toast.error("Failed to remove picture");
    }
  };

  const onSubmit = (formData: any) => {
    // "Photo is required" is defined once, in the requiredFields manifest —
    // this is that same predicate applied to the value held in local state.
    // RequestJoin (disablePhotoUpload) is exempt by design: photos come after
    // approval, and the backfill prompt collects them then.
    const photoMissing = isRequiredFieldMissing(PROFILE_PHOTO_FIELD_KEY, {
      profile: { profile_picture_url: profilePicture ?? null },
    });
    if (!disablePhotoUpload && photoMissing) {
      toast.error("Please upload a profile picture to continue");
      return;
    }

    // Combine profile picture and gallery pictures
    const allPictures = profilePicture
      ? [profilePicture, ...galleryPictures]
      : galleryPictures;

    onComplete({
      ...formData,
      profile_picture_url: profilePicture,
      profile_pictures: allPictures
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Personal Details</h3>
        
        {disablePhotoUpload && (
          <Alert>
            <AlertDescription>
              Photos can be added after your request is approved. We just need your basic info to get started.
            </AlertDescription>
          </Alert>
        )}

        {!disablePhotoUpload && (
        <>
        {/* Profile Picture Section */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold">Profile Picture *</Label>
            <p className="text-sm text-muted-foreground">Your main profile photo</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profilePictureSignedUrl ?? undefined} alt="Profile" />
              <AvatarFallback className="text-2xl">
                {data.first_name?.[0]?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>

            <div className="flex flex-col gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                id="profile-upload"
                disabled={uploadingProfile}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("profile-upload")?.click()}
                disabled={uploadingProfile}
              >
                {uploadingProfile ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {profilePicture ? "Change" : "Upload"}
                  </>
                )}
              </Button>
              {profilePicture && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveProfilePicture}
                  disabled={uploadingProfile}
                >
                  <X className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              )}
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Maximum file size: 5MB. Accepted formats: JPG, PNG, WEBP
          </p>
        </div>

        {/* Photo Gallery Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-semibold">Photo Gallery</Label>
              <p className="text-sm text-muted-foreground">
                Additional pictures ({galleryPictures.length}/5)
              </p>
            </div>
            {galleryPictures.length < 5 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingGallery}
                onClick={() => document.getElementById("gallery-upload")?.click()}
              >
                {uploadingGallery ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Add Photos
                  </>
                )}
              </Button>
            )}
          </div>
          
          {galleryPictures.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryPictures.map((path, index) => {
                const signed = gallerySignedUrls?.[index];
                return (
                  <div key={path} className="relative group">
                    <div className="relative aspect-square rounded-lg overflow-hidden border bg-muted">
                      {signed ? (
                        <img
                          src={signed}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRemoveGalleryPicture(path)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <p className="text-sm text-muted-foreground">
                No additional photos yet
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Add up to 5 more pictures to showcase yourself
              </p>
            </div>
          )}
          
          <input
            id="gallery-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleGalleryUpload}
            className="hidden"
          />
        </div>
        </>
        )}

        <div className="space-y-2">
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            {...register("first_name")}
            placeholder="Enter your first name"
          />
          {errors.first_name && (
            <p className="text-sm text-destructive">{errors.first_name.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="age">Age *</Label>
            <Input
              id="age"
              type="number"
              {...register("age", { valueAsNumber: true })}
              min={18}
              max={60}
            />
            {errors.age && (
              <p className="text-sm text-destructive">{errors.age.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Gender *</Label>
            <RadioGroup value={gender} onValueChange={(value) => setValue("gender", value as "male" | "female")}>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="male" />
                  <Label htmlFor="male" className="font-normal cursor-pointer">Male</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="female" />
                  <Label htmlFor="female" className="font-normal cursor-pointer">Female</Label>
                </div>
              </div>
            </RadioGroup>
            {errors.gender && (
              <p className="text-sm text-destructive">{errors.gender.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Height *</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Select value={String(watch("height_feet"))} onValueChange={(value) => setValue("height_feet", Number(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Feet" />
                </SelectTrigger>
                <SelectContent>
                  {[4, 5, 6, 7].map((ft) => (
                    <SelectItem key={ft} value={String(ft)}>{ft} ft</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Select value={String(watch("height_inches"))} onValueChange={(value) => setValue("height_inches", Number(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Inches" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i).map((inch) => (
                    <SelectItem key={inch} value={String(inch)}>{inch} in</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(errors.height_feet || errors.height_inches) && (
            <p className="text-sm text-destructive">Please select a valid height</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="ethnicity">Ethnicity *</Label>
          <Select value={watch("ethnicity")} onValueChange={(value) => setValue("ethnicity", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your ethnicity" />
            </SelectTrigger>
            <SelectContent>
              {ETHNICITY.map((eth) => (
                <SelectItem key={eth} value={eth}>{eth}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.ethnicity && (
            <p className="text-sm text-destructive">{errors.ethnicity.message}</p>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Location & Status</h3>
        
        <div className="space-y-2">
          <Label htmlFor="location">Location/City *</Label>
          <Select value={watch("location")} onValueChange={(value) => setValue("location", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your location" />
            </SelectTrigger>
            <SelectContent>
              {LOCATION_DFW.map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.location && (
            <p className="text-sm text-destructive">{errors.location.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="willing_to_relocate">Willing to relocate?</Label>
            <p className="text-sm text-muted-foreground">Are you open to moving to another city?</p>
          </div>
          <Switch
            id="willing_to_relocate"
            checked={watch("willing_to_relocate")}
            onCheckedChange={(checked) => setValue("willing_to_relocate", checked)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="citizenship_status">Citizenship Status *</Label>
          <Select value={watch("citizenship_status")} onValueChange={(value) => setValue("citizenship_status", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your status" />
            </SelectTrigger>
            <SelectContent>
              {CITIZENSHIP_STATUS.map((status) => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.citizenship_status && (
            <p className="text-sm text-destructive">{errors.citizenship_status.message}</p>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Marital Background</h3>
        
        <div className="space-y-2">
          <Label htmlFor="marital_status">Marital Status *</Label>
          <Select value={watch("marital_status")} onValueChange={(value) => setValue("marital_status", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your marital status" />
            </SelectTrigger>
            <SelectContent>
              {MARITAL_STATUS.map((status) => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.marital_status && (
            <p className="text-sm text-destructive">{errors.marital_status.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="has_children">Do you have children?</Label>
          </div>
          <Switch
            id="has_children"
            checked={hasChildren}
            onCheckedChange={(checked) => setValue("has_children", checked)}
          />
        </div>

        {hasChildren && (
          <div className="space-y-2">
            <Label htmlFor="children_count">How many children? *</Label>
            <Input
              id="children_count"
              type="number"
              {...register("children_count", { valueAsNumber: true })}
              min={0}
              max={10}
            />
            {errors.children_count && (
              <p className="text-sm text-destructive">{errors.children_count.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            continueLabel
          )}
        </Button>
      </div>
    </form>
  );
}
